import { useQueries } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import axios from '@pepper/utils/src/function/axiosInstance'

import { DateRange, Device, KeywordQuery } from '../ContentRoi/types'
import { SEO_SERVICE_BASE_URL } from '@pepper/utils/src/api/config'

const _getErrorString = (error: AxiosError<{ description?: string }>) =>
  error.response?.data?.description || 'Something went wrong'

type Args = {
  companyId: string
  period: DateRange
  date: string
  domainId?: string
  deviceCategory?: Device
  queryType?: KeywordQuery
}

const fetchSpends = async ({
  companyId,
  period,
  date,
  domainId,
  deviceCategory,
}: Args) => {
  try {
    const url = `${SEO_SERVICE_BASE_URL}/companies/${companyId}/analytics-roi/spends`
    const response = await axios.get(url, {
      params: {
        date,
        interval: period,
        domain_id: domainId,
        device_category: deviceCategory,
      },
    })
    return response.data
  } catch (error) {
    throw new Error(_getErrorString(error))
  }
}

export const fetchStats = async ({
  companyId,
  domainId,
  date,
  deviceCategory,
  period,
}: Args) => {
  try {
    const url = `${SEO_SERVICE_BASE_URL}/companies/${companyId}/analytics-roi`
    const response = await axios.get(url, {
      params: {
        date,
        interval: period,
        domain_id: domainId,
        device_category:
          deviceCategory === 'all_devices' ? undefined : deviceCategory,
      },
    })
    return response.data
  } catch (error) {
    throw new Error(_getErrorString(error))
  }
}

const fetchPagesDistribution = async ({
  companyId,
  period,
  domainId,
  deviceCategory,
  date,
}: Args) => {
  try {
    const url = `${SEO_SERVICE_BASE_URL}/companies/${companyId}/analytics-roi/top-organic-pages/distribution`
    const response = await axios.get(url, {
      params: {
        date,
        interval: period,
        domain_id: domainId,
        device_category: deviceCategory,
      },
    })
    return response.data
  } catch (error) {
    throw new Error(_getErrorString(error))
  }
}

const fetchKeywordsDistribution = async ({
  companyId,
  period,
  domainId,
  date,
  queryType,
  deviceCategory,
}: Args) => {
  try {
    const url = `${SEO_SERVICE_BASE_URL}/companies/${companyId}/analytics-roi/top-organic-keywords/distribution`
    const response = await axios.get(url, {
      params: {
        date,
        interval: period,
        query_type: queryType,
        domain_id: domainId,
        device_category: deviceCategory,
      },
    })
    return response.data
  } catch (error) {
    throw new Error(_getErrorString(error))
  }
}

type UseRoiProps = {
  companyId: string
  isEnabled: boolean
  period: DateRange
  date: string
  domainId: string
  deviceCategory?: Device
  queryType?: KeywordQuery
}

export const useRoi = ({
  companyId,
  isEnabled,
  period,
  date,
  domainId,
  deviceCategory,
  queryType,
}: UseRoiProps) => {
  return useQueries({
    queries: [
      {
        queryKey: [
          'roi/spends',
          period,
          companyId,
          date,
          domainId,
          deviceCategory,
        ],
        queryFn: () =>
          fetchSpends({
            companyId,
            period,
            date,
            domainId,
            deviceCategory,
          }),
        enabled: isEnabled,
      },
      {
        queryKey: [
          'roi/stats',
          companyId,
          period,
          domainId,
          date,
          deviceCategory,
        ],
        queryFn: () =>
          fetchStats({
            companyId,
            period,
            domainId,
            date,
            deviceCategory,
          }),
        enabled: isEnabled,
      },
      {
        queryKey: [
          'roi/top-organic-pages-distribution',
          companyId,
          period,
          domainId,
          deviceCategory,
          date,
        ],
        queryFn: () =>
          fetchPagesDistribution({
            companyId,
            period,
            domainId,
            deviceCategory,
            date,
          }),
        enabled: isEnabled,
        refetchOnWindowFocus: false,
      },
      {
        queryKey: [
          'roi/top-organic-keywords-distribution',
          companyId,
          period,
          domainId,
          date,
          queryType,
          deviceCategory,
        ],
        queryFn: () =>
          fetchKeywordsDistribution({
            companyId,
            period,
            domainId,
            date,
            queryType,
            deviceCategory,
          }),
        enabled: isEnabled,
        refetchOnWindowFocus: false,
      },
    ],
  })
}
